/**
 * Batch Weight API service for collection / search product grids.
 * One POST per visible page of SKUs; cache + AbortController + debounce.
 */

const BATCH_PATH = '/public/products/weights/batch';
const DEBOUNCE_MS = 300;
const MAX_DEBOUNCE_WAIT_MS = 1200;
const EMPTY_DISPLAY = '—';
const LOADING_DISPLAY = '...';

/** @type {Set<string>} */
const inFlightSkus = new Set();

/** @type {AbortController | null} */
let activeController = null;

/** @type {string} */
let activeRequestKey = '';

/** @type {Promise<Map<string, number | null>> | null} */
let activeRequestPromise = null;

/** @type {number} */
let requestGeneration = 0;

/** @type {ReturnType<typeof setTimeout> | null} */
let debounceTimer = null;

/** @type {number} */
let debounceStartedAt = 0;

/** @type {MutationObserver | null} */
let gridObserver = null;

/** @type {boolean} */
let initialized = false;

/** @type {boolean} */
let isApplyingDom = false;

/** @type {ParentNode} */
let pendingHydrateScope = document;

/**
 * @typedef {{
 *   enabled: boolean,
 *   baseUrl: string,
 *   timeoutMs: number,
 *   debug: boolean,
 * }} BatchWeightConfig
 */

/** @returns {BatchWeightConfig} */
function readConfig() {
  const el = document.querySelector('[data-batch-weight-config]');
  /** @type {BatchWeightConfig} */
  const fallback = {
    enabled: false,
    baseUrl: '',
    timeoutMs: 8000,
    debug: false,
  };
  if (!el) return fallback;
  try {
    const parsed = JSON.parse(el.textContent || '{}');
    return {
      enabled: Boolean(parsed.enabled),
      baseUrl: String(parsed.baseUrl || '').trim(),
      timeoutMs: Math.max(1000, Number(parsed.timeoutMs) || 8000),
      debug: Boolean(parsed.debug),
    };
  } catch {
    return fallback;
  }
}

/** @param {...unknown} args */
function debugLog(...args) {
  const cfg = readConfig();
  if (!cfg.debug) return;
  console.log('[BatchWeight]', ...args);
}

/** @param {string} base @param {string} path */
function joinUrl(base, path) {
  const b = String(base || '').replace(/\/+$/, '');
  let p = String(path || '');
  if (!p) return b;
  if (/^https?:\/\//i.test(p)) return p;
  if (p.charAt(0) !== '/') p = '/' + p;
  return b ? b + p : p;
}

/** @param {string} url */
function requestHeaders(url) {
  /** @type {Record<string, string>} */
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  try {
    const host = new URL(url, window.location.origin).hostname.toLowerCase();
    if (host.includes('ngrok')) headers['ngrok-skip-browser-warning'] = 'true';
  } catch {
    /* ignore */
  }
  return headers;
}

/** @param {unknown} value */
function normalizeSku(value) {
  const sku = String(value || '').trim();
  return sku || '';
}

/** @param {string[]} skus */
function requestKeyForSkus(skus) {
  return skus
    .map((s) => s.toLowerCase())
    .sort()
    .join('|');
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Extract a single numeric weight from a payload fragment.
 * @param {unknown} node
 * @returns {number | null}
 */
function extractMinFromNode(node) {
  if (node == null) return null;

  const direct = toFiniteNumber(node);
  if (direct != null && direct > 0) return direct;

  if (Array.isArray(node)) {
    let min = null;
    for (const item of node) {
      const n = extractMinFromNode(item);
      if (n == null || n <= 0) continue;
      min = min == null ? n : Math.min(min, n);
    }
    return min;
  }

  if (typeof node !== 'object') return null;
  const obj = /** @type {Record<string, unknown>} */ (node);

  const preferredKeys = [
    'minWeight',
    'min_weight',
    'minimumWeight',
    'minimum_weight',
    'minWeightGrams',
    'min_weight_grams',
    'weight',
    'weight_g',
    'gross_weight',
    'net_weight',
  ];

  for (const key of preferredKeys) {
    if (!(key in obj)) continue;
    const raw = obj[key];
    // Some APIs return minWeight as nested objects/arrays (e.g. { value: 1.42 } or [1.42]).
    const n = extractMinFromNode(raw);
    if (n != null && n > 0) return n;
  }

  // Flexible fallback for APIs using alternate key casing/naming (e.g. minweight, min_weight_gm)
  for (const [rawKey, rawValue] of Object.entries(obj)) {
    const key = String(rawKey || '')
      .replace(/[\s_-]+/g, '')
      .toLowerCase();
    if (!key.includes('weight')) continue;
    if (key.includes('minimum') || key.includes('min') || key === 'weight') {
      const n = toFiniteNumber(rawValue);
      if (n != null && n > 0) return n;
    }
  }

  for (const listKey of ['availableWeights', 'availableOptions', 'weights', 'items', 'results', 'data']) {
    if (!(listKey in obj)) continue;
    const fromList = extractMinFromNode(obj[listKey]);
    if (fromList != null) return fromList;
  }

  return null;
}

/**
 * @param {Record<string, unknown>} obj
 * @returns {string}
 */
function extractSkuFromObject(obj) {
  const keys = [
    'sku',
    'SKU',
    'productSku',
    'product_sku',
    'variantSku',
    'variant_sku',
    'itemCode',
    'item_code',
    'code',
  ];
  for (const key of keys) {
    if (!(key in obj)) continue;
    const normalized = normalizeSku(obj[key]);
    if (normalized) return normalized;
  }
  return '';
}

/**
 * Handles object maps keyed by SKU, e.g. { "SKU1": {minWeight:..}, ... }.
 * @param {Record<string, unknown>} source
 * @param {Set<string>} requested
 * @param {(sku: string, value: unknown) => void} assign
 */
function assignFromSkuMap(source, requested, assign) {
  for (const [key, value] of Object.entries(source)) {
    const normalizedKey = normalizeSku(key);
    if (!normalizedKey) continue;
    if (['success', 'status', 'message', 'error', 'errors', 'meta'].includes(normalizedKey.toLowerCase())) {
      continue;
    }
    if (!requested.size || requested.has(normalizedKey.toLowerCase())) {
      assign(normalizedKey, value);
    }
  }
}

/**
 * Parse batch API response into Map(sku -> minWeight|null).
 * @param {unknown} payload
 * @param {string[]} requestedSkus
 * @returns {Map<string, number | null>}
 */
function parseBatchResponse(payload, requestedSkus) {
  /** @type {Map<string, number | null>} */
  const result = new Map();
  const requested = new Set(requestedSkus.map((s) => s.toLowerCase()));

  /**
   * @param {string} sku
   * @param {unknown} value
   */
  const assign = (sku, value) => {
    const normalized = normalizeSku(sku);
    if (!normalized) return;
    const min = extractMinFromNode(value);
    if (min != null && min > 0) {
      result.set(normalized, min);
    }
  };

  if (Array.isArray(payload)) {
    for (const item of payload) {
      if (!item || typeof item !== 'object') continue;
      const obj = /** @type {Record<string, unknown>} */ (item);
      const sku = extractSkuFromObject(obj);
      if (sku) assign(String(sku), obj);
    }
    return result;
  }

  if (!payload || typeof payload !== 'object') return result;
  const root = /** @type {Record<string, unknown>} */ (payload);

  for (const listKey of ['products', 'results', 'items', 'data', 'weights']) {
    const listOrMap = root[listKey];
    if (!listOrMap) continue;

    if (Array.isArray(listOrMap)) {
      for (const item of listOrMap) {
        if (!item || typeof item !== 'object') continue;
        const obj = /** @type {Record<string, unknown>} */ (item);
        const sku = extractSkuFromObject(obj);
        if (sku) assign(String(sku), obj);
      }
    } else if (typeof listOrMap === 'object') {
      assignFromSkuMap(/** @type {Record<string, unknown>} */ (listOrMap), requested, assign);
      // Support API shape: { data: { items: [{ sku, minWeight, ... }] } }
      const nestedItems = /** @type {Record<string, unknown>} */ (listOrMap).items;
      if (Array.isArray(nestedItems)) {
        for (const item of nestedItems) {
          if (!item || typeof item !== 'object') continue;
          const obj = /** @type {Record<string, unknown>} */ (item);
          const sku = extractSkuFromObject(obj);
          if (sku) assign(String(sku), obj);
        }
      }
    }
    if (result.size) return result;
  }

  assignFromSkuMap(root, requested, assign);
  if (result.size) return result;

  for (const [key, value] of Object.entries(root)) {
    if (['success', 'status', 'message', 'error', 'errors', 'meta'].includes(key)) continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const nested = /** @type {Record<string, unknown>} */ (value);
    const nestedSku = extractSkuFromObject(nested);
    if (nestedSku) assign(nestedSku, nested);
  }

  if (!result.size && requestedSkus.length === 1) {
    const min = extractMinFromNode(root);
    const onlySku = requestedSkus[0];
    if (onlySku) result.set(onlySku, min);
  }

  return result;
}

/** @param {number | null | undefined} value */
function formatWeight(value) {
  if (value == null || !Number.isFinite(value) || value <= 0) return EMPTY_DISPLAY;
  const rounded = Math.round(value * 1000) / 1000;
  return `${rounded.toFixed(3)} g`;
}

/**
 * Roots that contain collection / search product grids only.
 * @returns {Element[]}
 */
function getGridRoots() {
  const selectors = [
    '.plp-product-grid',
    '.wizzy-search-results-list',
    '[data-testid="product-grid"]',
    'results-list',
  ];
  /** @type {Element[]} */
  const roots = [];
  for (const selector of selectors) {
    document.querySelectorAll(selector).forEach((el) => {
      if (!roots.includes(el)) roots.push(el);
    });
  }
  return roots;
}

/**
 * Ensure cards expose data-weight-sku (from attribute or visible SKU text).
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function collectWeightCards(root) {
  const scopeSelector =
    '.plp-product-grid, .wizzy-search-results-list, [data-testid="product-grid"], results-list';
  const ignoreSelector =
    '.predictive-search, .wizzy-autocomplete-wrapper, .quick-view, #quick-view-modal';

  /** @type {HTMLElement[]} */
  const cards = [];

  root.querySelectorAll('.plp-card, [data-weight-sku]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (!el.closest(scopeSelector)) return;
    if (el.closest(ignoreSelector)) return;

    let sku = normalizeSku(el.getAttribute('data-weight-sku'));
    if (!sku) {
      const skuText = el.querySelector('.plp-card__sku, .inventory-product-row__sku-value');
      if (skuText) {
        const match = String(skuText.textContent || '').match(/SKU:\s*(.+)$/i);
        sku = normalizeSku(match ? match[1] : skuText.textContent);
      }
      if (sku) el.setAttribute('data-weight-sku', sku);
    }

    if (!sku) return;
    if (!cards.includes(el)) cards.push(el);
  });

  return cards;
}

/**
 * @param {HTMLElement} card
 * @param {'loading' | 'ready' | 'error'} state
 * @param {string} text
 */
function setCardWeight(card, state, text) {
  isApplyingDom = true;
  try {
    card.dataset.minWeightState = state;
    if (state === 'ready' || state === 'error') {
      card.dataset.weightHydrated = 'true';
    }

    const targets = card.querySelectorAll('[data-min-weight-value]');
    if (!targets.length) {
      const legacy = card.querySelector('[data-wizzy-min-weight-value]');
      if (legacy) legacy.textContent = text;
      return;
    }
    targets.forEach((el) => {
      el.textContent = text;
      if (state === 'loading') el.setAttribute('data-min-weight-loading', '');
      else el.removeAttribute('data-min-weight-loading');
    });
    debugLog('Render weight', {
      sku: card.getAttribute('data-weight-sku') || '',
      state,
      value: text,
      targetCount: targets.length,
      updatedSuccessfully: targets.length > 0,
    });
  } finally {
    // Let the browser flush mutations before re-enabling the observer handler
    queueMicrotask(() => {
      isApplyingDom = false;
    });
  }
}

/**
 * @param {string[]} skus
 * @param {BatchWeightConfig} cfg
 * @returns {Promise<Map<string, number | null>>}
 */
function fetchBatchWeights(skus, cfg) {
  const key = requestKeyForSkus(skus);

  // Reuse identical in-flight request instead of aborting it
  if (activeRequestPromise && activeRequestKey === key) {
    debugLog('Reusing in-flight request', { key, skus });
    return activeRequestPromise;
  }

  // If current in-flight already covers all requested SKUs, reuse it
  if (activeRequestPromise && skus.every((sku) => inFlightSkus.has(sku.toLowerCase()))) {
    debugLog('Requested SKUs already in flight — reuse', { skus });
    return activeRequestPromise;
  }

  // Only abort when the SKU set actually changed (new page / filter result)
  if (activeController) {
    debugLog('Aborting previous request for new SKU set', {
      previous: activeRequestKey,
      next: key,
    });
    activeController.abort();
  }

  activeController = new AbortController();
  const controller = activeController;
  activeRequestKey = key;
  inFlightSkus.clear();
  skus.forEach((sku) => inFlightSkus.add(sku.toLowerCase()));

  const timeoutId = window.setTimeout(() => controller.abort(), cfg.timeoutMs);
  const url = joinUrl(cfg.baseUrl, BATCH_PATH);
  if (!url || !cfg.baseUrl) {
    return Promise.reject(new Error('Batch Weight API base URL is not configured'));
  }

  const started = performance.now();
  const generation = ++requestGeneration;
  debugLog('API Request', { url, skus, generation, key });

  activeRequestPromise = (async () => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: requestHeaders(url),
        body: JSON.stringify({ skus }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Weight API HTTP ${response.status}`);
      }

      let payload;
      try {
        payload = await response.json();
      } catch {
        throw new Error('Weight API returned invalid JSON');
      }

      const parsed = parseBatchResponse(payload, skus);
      debugLog('API Response', {
        durationMs: Math.round(performance.now() - started),
        count: parsed.size,
        payload,
        generation,
      });
      return parsed;
    } finally {
      window.clearTimeout(timeoutId);
      if (activeController === controller) {
        activeController = null;
        activeRequestKey = '';
        activeRequestPromise = null;
        inFlightSkus.clear();
      }
    }
  })();

  return activeRequestPromise;
}

/**
 * Apply cached/fetched values to cards matching SKU.
 * @param {HTMLElement[]} cards
 * @param {Map<string, number | null>} values
 */
function applyWeightsToCards(cards, values) {
  let updated = 0;
  for (const card of cards) {
    const sku = normalizeSku(card.getAttribute('data-weight-sku'));
    if (!sku) {
      debugLog('Skip card: missing SKU', { card });
      setCardWeight(card, 'error', EMPTY_DISPLAY);
      continue;
    }

    let matchedValue = undefined;
    if (values.has(sku)) {
      matchedValue = values.get(sku);
    } else {
      for (const [key, value] of values.entries()) {
        if (key.toLowerCase() === sku.toLowerCase()) {
          matchedValue = value;
          break;
        }
      }
    }

    if (matchedValue === undefined) {
      debugLog('No matching weight for SKU', {
        cardSku: sku,
        apiSkus: Array.from(values.keys()),
      });
      setCardWeight(card, 'error', EMPTY_DISPLAY);
      continue;
    }

    if (matchedValue != null && Number.isFinite(matchedValue) && matchedValue > 0) {
      debugLog('Matched SKU -> weight', { sku, weight: matchedValue });
      setCardWeight(card, 'ready', formatWeight(matchedValue));
    } else {
      debugLog('Invalid/non-positive weight', { sku, matchedValue });
      setCardWeight(card, 'error', EMPTY_DISPLAY);
    }
    updated += 1;
  }
  debugLog('Updated Product Count', updated);
}

/**
 * Clear loading placeholders on in-scope cards that have no SKU.
 * @param {ParentNode} root
 */
function clearOrphanLoaders(root) {
  const nodes = root.querySelectorAll(
    '.plp-product-grid [data-min-weight-loading], .wizzy-search-results-list [data-min-weight-loading], [data-testid="product-grid"] [data-min-weight-loading]'
  );
  nodes.forEach((el) => {
    const card = el.closest('[data-min-weight-state], .plp-card');
    if (!(card instanceof HTMLElement)) return;
    if (normalizeSku(card.getAttribute('data-weight-sku'))) return;
    setCardWeight(card, 'error', EMPTY_DISPLAY);
  });
}

/**
 * True when mutation adds meaningful product cards / SKUs (not our own weight text updates).
 * @param {MutationRecord[]} mutations
 */
function mutationNeedsHydrate(mutations) {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes' && mutation.attributeName === 'data-weight-sku') {
      const el = mutation.target;
      if (el instanceof HTMLElement && normalizeSku(el.getAttribute('data-weight-sku'))) {
        // New/changed SKU that is not yet hydrated
        if (el.dataset.weightHydrated !== 'true') return true;
      }
      continue;
    }

    if (mutation.type !== 'childList') continue;

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.('[data-weight-sku], .plp-card, .plp-product-grid__item, .wizzy-result-product')) {
        return true;
      }
      if (node.querySelector?.('[data-weight-sku], .plp-card, .plp-product-grid__item')) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Core hydrate: collect SKUs in scope, use cache, batch-fetch misses.
 * @param {ParentNode} [scope]
 */
async function hydrate(scope = document) {
  const cfg = readConfig();
  if (!cfg.enabled) return;

  clearOrphanLoaders(scope);

  const cards = collectWeightCards(scope);
  if (!cards.length) return;

  /** @type {string[]} */
  const allSkus = [];
  /** @type {string[]} */
  const misses = [];

  for (const card of cards) {
    const sku = normalizeSku(card.getAttribute('data-weight-sku'));
    if (!sku) {
      setCardWeight(card, 'error', EMPTY_DISPLAY);
      continue;
    }
    if (!allSkus.includes(sku)) allSkus.push(sku);

    if (!misses.includes(sku)) misses.push(sku);
    setCardWeight(card, 'loading', LOADING_DISPLAY);
  }

  debugLog('Collected SKUs', allSkus);
  debugLog('Batch Request SKUs', misses);

  if (!misses.length) {
    debugLog('No API call — no valid SKUs in scope');
    return;
  }

  const generation = requestGeneration;

  try {
    const fetched = await fetchBatchWeights(misses, cfg);

    const liveCards = collectWeightCards(document).filter((card) => {
      const sku = normalizeSku(card.getAttribute('data-weight-sku'));
      return Boolean(sku && misses.some((m) => m.toLowerCase() === sku.toLowerCase()));
    });
    applyWeightsToCards(liveCards, fetched);
  } catch (error) {
    if (error && /** @type {Error} */ (error).name === 'AbortError') {
      debugLog('Request aborted', { generation });
      return;
    }

    debugLog('API Error', error);
    // Do not cache failures — allow retry on next hydrate
    for (const card of collectWeightCards(document)) {
      const sku = normalizeSku(card.getAttribute('data-weight-sku'));
      if (!sku) continue;
      if (!misses.some((m) => m.toLowerCase() === sku.toLowerCase())) continue;
      setCardWeight(card, 'error', EMPTY_DISPLAY);
    }
  }
}

/** @param {ParentNode} [scope] */
function scheduleHydrate(scope = document) {
  const cfg = readConfig();
  if (!cfg.enabled) return;

  pendingHydrateScope = scope;
  const now = Date.now();
  if (!debounceStartedAt) debounceStartedAt = now;

  if (debounceTimer) window.clearTimeout(debounceTimer);

  const elapsed = now - debounceStartedAt;
  const wait = elapsed >= MAX_DEBOUNCE_WAIT_MS ? 0 : DEBOUNCE_MS;

  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    debounceStartedAt = 0;
    const target = pendingHydrateScope || document;
    hydrate(target);
  }, wait);
}

function observeGrids() {
  if (gridObserver) gridObserver.disconnect();

  gridObserver = new MutationObserver((mutations) => {
    if (isApplyingDom) return;
    if (!mutationNeedsHydrate(mutations)) return;
    debugLog('Pagination / DOM update triggered');
    scheduleHydrate(document);
  });

  const roots = getGridRoots();
  const observeOpts = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-weight-sku'],
  };

  if (!roots.length) {
    const main = document.getElementById('MainContent') || document.body;
    gridObserver.observe(main, observeOpts);
    return;
  }

  for (const root of roots) {
    gridObserver.observe(root, observeOpts);
  }
}

function bindThemeEvents() {
  document.addEventListener('filter:update', () => {
    debugLog('FilterUpdate triggered');
    window.setTimeout(() => {
      observeGrids();
      scheduleHydrate(document);
    }, 80);
  });

  document.addEventListener('shopify:section:load', () => {
    observeGrids();
    scheduleHydrate(document);
  });

  window.addEventListener('popstate', () => {
    debugLog('popstate triggered');
    window.setTimeout(() => {
      observeGrids();
      scheduleHydrate(document);
    }, 80);
  });
}

function init() {
  if (initialized) return;
  initialized = true;

  const cfg = readConfig();
  if (!cfg.enabled) {
    debugLog('Disabled via theme settings');
    return;
  }

  if (!cfg.baseUrl) {
    debugLog('Enabled but Base URL is empty — no requests will be made');
    return;
  }

  bindThemeEvents();
  observeGrids();
  scheduleHydrate(document);

  // Extra passes for late Wizzy / AJAX grid injection
  window.setTimeout(() => {
    observeGrids();
    scheduleHydrate(document);
  }, 800);
  window.setTimeout(() => {
    observeGrids();
    hydrate(document);
  }, 1600);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export { hydrate, scheduleHydrate, formatWeight };

(/** @type {any} */ (window)).AoneBatchWeight = {
  hydrate,
  scheduleHydrate,
};
